// apps/client/src/components/dormancy/DormantLoansSection.tsx
//
// The "Dormant — needs review" surface on the Discharges page. Loans the dormancy
// engine retired for 24-month commission silence (reversible). The subscriber
// resolves each to one of three outcomes:
//   • Still active  → reactivate (loan leaves the queue, returns to the book)
//   • Keep dormant  → confirm; stays parked (marked reviewed)
//   • Discharged    → confirm gone; pick a loss reason → appears in the Discharges tab
//
// Presentational: data + resolve() come from useDormantLoans (lifted to the page so
// the tab badge can show the count). Reuses the shared ContactTimeline for outreach
// logging. The discharge loss reason is persisted server-side by resolveDormantLoan
// (§4.5 repoint) — passed into the SAME resolve() call, NOT a separate setLossReason
// call (which removed the third ungate-able setLossReason caller).
import React, { useMemo, useState } from 'react';
import { Moon, Check, ChevronDown, ChevronRight, Banknote, Clock } from 'lucide-react';
import ContactTimeline from '../contacts/ContactTimeline';
import type { DormantLoanRecord, DormantResolution } from '../../hooks/useDormantLoans';
import type { LoanContactEntry, MutateContactInput } from '../../hooks/useLoanContacts';

const LOSS_REASON_LABELS: Record<string, string> = {
  lost_to_competitor: 'Lost to competitor',
  property_sold: 'Property sold',
  paid_off: 'Paid off',
  debt_consolidation: 'Debt consolidation',
  investment_sold: 'Investment sold',
  relationship_settlement: 'Relationship settlement',
  customer_moved: 'Customer moved',
  bridging_completed: 'Bridging completed',
  deceased: 'Deceased',
  unknown: 'Unknown',
};
const LOSS_REASONS = Object.keys(LOSS_REASON_LABELS);

const fmt$ = (cents: number) => `$${Math.round((cents || 0) / 100).toLocaleString('en-AU')}`;
const fmtMonth = (d: Date | null) =>
  d ? d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }) : '—';

interface Props {
  companyId: string;
  dormantLoans: DormantLoanRecord[];
  loading: boolean;
  error: string | null;
  totalLoanValueCents: number;
  needsReviewCount: number;
  resolve: (
    loanIds: string[],
    resolution: DormantResolution,
    notes?: string | null,
    reason?: string | null
  ) => Promise<{ reasonWriteFailed?: boolean }>;
  contactsByLoan: Record<string, LoanContactEntry[]>;
  mutateContact: (input: MutateContactInput) => Promise<{ ok: boolean; entryId: string }>;
  contactBrokerList: { id: string; name: string }[];
  currentUser: { uid?: string; displayName?: string; email?: string; role?: string };
}

interface RowDraft {
  resolution: DormantResolution | null;
  reason: string;
  notes: string;
}

const DormantLoansSection: React.FC<Props> = ({
  companyId,
  dormantLoans,
  loading,
  error,
  totalLoanValueCents,
  needsReviewCount,
  resolve,
  contactsByLoan,
  mutateContact,
  contactBrokerList,
  currentUser,
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  // Sortable column headers — same interaction as the Discharges table.
  type SortCol = 'customer' | 'lender' | 'amount' | 'lastCommission' | 'silent' | 'broker';
  const [sortColumn, setSortColumn] = useState<SortCol>('amount');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const handleSort = (column: SortCol) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };
  const SortIndicator = ({ column }: { column: SortCol }) =>
    column !== sortColumn ? (
      <span className="ml-1 text-gray-600">↕</span>
    ) : (
      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
    );

  const sortedLoans = useMemo(() => {
    const arr = [...dormantLoans];
    arr.sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      switch (sortColumn) {
        case 'customer': av = (a.customerName || '').toLowerCase(); bv = (b.customerName || '').toLowerCase(); break;
        case 'lender': av = (a.lender || '').toLowerCase(); bv = (b.lender || '').toLowerCase(); break;
        case 'amount': av = a.loanAmountCents || 0; bv = b.loanAmountCents || 0; break;
        case 'lastCommission': av = a.lastCommissionDate ? a.lastCommissionDate.getTime() : 0; bv = b.lastCommissionDate ? b.lastCommissionDate.getTime() : 0; break;
        case 'silent': av = a.monthsSilent ?? -1; bv = b.monthsSilent ?? -1; break;
        case 'broker': av = (a.broker || '').toLowerCase(); bv = (b.broker || '').toLowerCase(); break;
        default: av = 0; bv = 0;
      }
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDirection === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDirection === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
    return arr;
  }, [dormantLoans, sortColumn, sortDirection]);

  const draftFor = (loanId: string): RowDraft => drafts[loanId] || { resolution: null, reason: '', notes: '' };
  const setDraft = (loanId: string, patch: Partial<RowDraft>) =>
    setDrafts((prev) => ({ ...prev, [loanId]: { ...draftFor(loanId), ...patch } }));

  const handleResolve = async (loan: DormantLoanRecord) => {
    const d = draftFor(loan.loanId);
    if (!d.resolution) return;
    if (d.resolution === 'discharged' && !d.reason) return;
    setSavingId(loan.loanId);
    setRowError(null);
    try {
      // ONE server call. resolveDormantLoan owns BOTH the state transition AND
      // (for 'discharged') the loss-reason write — transition first, reason after
      // an affected-row-verified discharge (§4.5). The component no longer calls
      // setLossReason directly.
      const { reasonWriteFailed } = await resolve(
        [loan.loanId],
        d.resolution,
        d.notes || null,
        d.resolution === 'discharged' ? d.reason : null
      );
      // Discharge committed but the reason write failed: recoverable, not a
      // failed resolve. Warn (the loan still left the queue) and keep the row
      // collapsed so the operator can re-categorise on the Discharges tab.
      if (reasonWriteFailed) {
        setRowError(
          'Loan discharged, but saving the loss reason failed — set it from the Discharges tab.'
        );
      }
      setExpanded(null);
    } catch (e: any) {
      setRowError(e instanceof Error ? e.message : 'Failed to resolve loan');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg shadow-lg p-10 border border-gray-700 text-center text-gray-400">
        Loading dormant loans…
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
        <h3 className="text-red-300 font-semibold mb-1">Couldn’t load dormant loans</h3>
        <p className="text-red-200 text-sm">{error}</p>
      </div>
    );
  }
  if (dormantLoans.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg shadow-lg p-10 border border-gray-700 text-center">
        <Moon className="w-8 h-8 text-blue-400 mx-auto mb-3" strokeWidth={1.5} />
        <h3 className="text-white font-semibold">No dormant loans to review</h3>
        <p className="text-gray-400 text-sm mt-1">
          Nothing has gone 24+ months without commission for the current filters.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
          <p className="text-sm font-medium text-gray-400">Dormant loans</p>
          <p className="text-4xl font-bold text-white mt-2">{dormantLoans.length}</p>
          <p className="text-xs text-gray-500 mt-1">no commission 24+ months</p>
        </div>
        <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
          <p className="text-sm font-medium text-gray-400">Retired value</p>
          <p className="text-4xl font-bold text-blue-400 mt-2">{fmt$(totalLoanValueCents)}</p>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><Banknote className="w-3 h-3" /> off the active book</p>
        </div>
        <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
          <p className="text-sm font-medium text-gray-400">Needs review</p>
          <p className="text-4xl font-bold text-amber-400 mt-2">{needsReviewCount}</p>
          <p className="text-xs text-gray-500 mt-1">awaiting your decision</p>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4 flex items-start gap-3">
        <Moon className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-blue-300 leading-relaxed">
          These loans haven’t paid commission in 24+ months, so they’re retired from your active Loan Book —
          but reversibly. Confirm each: <span className="font-semibold text-gray-200">Still active</span> puts it
          back, <span className="font-semibold text-gray-200">Keep dormant</span> parks it, and{' '}
          <span className="font-semibold text-gray-200">Discharged</span> records it as gone. If a loan ever pays
          again it reactivates automatically.
        </p>
      </div>

      {rowError && (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-3 text-sm text-red-200">{rowError}</div>
      )}

      {/* Table */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900 border-b border-gray-700">
              <tr>
                <th onClick={() => handleSort('customer')} className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-800">Client<SortIndicator column="customer" /></th>
                <th onClick={() => handleSort('lender')} className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-800">Lender<SortIndicator column="lender" /></th>
                <th onClick={() => handleSort('amount')} className="px-6 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-800">Amount<SortIndicator column="amount" /></th>
                <th onClick={() => handleSort('lastCommission')} className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-800">Last commission<SortIndicator column="lastCommission" /></th>
                <th onClick={() => handleSort('silent')} className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-800">Silent<SortIndicator column="silent" /></th>
                <th onClick={() => handleSort('broker')} className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-800">Broker<SortIndicator column="broker" /></th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {sortedLoans.map((loan) => {
                const isOpen = expanded === loan.loanId;
                const d = draftFor(loan.loanId);
                return (
                  <React.Fragment key={loan.loanId}>
                    <tr
                      className="hover:bg-gray-750 transition-colors cursor-pointer"
                      onClick={() => setExpanded(isOpen ? null : loan.loanId)}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-white">{loan.customerName}</div>
                        <div className="text-xs text-gray-500">loan #{loan.loanId}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">{loan.lender || '—'}</td>
                      <td className="px-6 py-4 text-sm text-right tabular-nums text-white">{fmt$(loan.loanAmountCents)}</td>
                      <td className="px-6 py-4 text-sm text-gray-300">{fmtMonth(loan.lastCommissionDate)}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-xs rounded-full bg-amber-600/20 text-amber-400 whitespace-nowrap">
                          {loan.monthsSilent != null ? `${loan.monthsSilent} mo` : '24+ mo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">{loan.broker || '—'}</td>
                      <td className="px-6 py-4">
                        {loan.reviewedResolution === 'keep_dormant' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border bg-blue-500/15 text-blue-300 border-blue-500/30">
                            <Clock className="w-3 h-3" /> Reviewed · dormant
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border bg-amber-500/15 text-amber-300 border-amber-500/30">
                            Needs review
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-500">
                        {isOpen ? <ChevronDown className="w-4 h-4 inline" /> : <ChevronRight className="w-4 h-4 inline" />}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-850">
                        <td colSpan={8} className="px-6 py-5 bg-gray-900/40">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                            {/* LEFT: resolver */}
                            <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                              <h4 className="text-xs uppercase tracking-wide text-blue-400 font-bold mb-3">
                                Resolve dormant loan
                              </h4>
                              <div className="space-y-2">
                                {(
                                  [
                                    ['still_active', 'Still active', 'Producing commission — dormancy was a false positive. Reactivates the loan.'],
                                    ['keep_dormant', 'Keep dormant', 'Client may return; park it and monitor.'],
                                    ['discharged', 'Discharged', 'Loan is gone — record a loss reason. Moves to the Discharges tab.'],
                                  ] as const
                                ).map(([value, label, help]) => (
                                  <label
                                    key={value}
                                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition ${
                                      d.resolution === value
                                        ? 'border-blue-500/60 bg-blue-500/10'
                                        : 'border-gray-700 hover:bg-gray-700/40'
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name={`dormant-${loan.loanId}`}
                                      checked={d.resolution === value}
                                      onChange={() => setDraft(loan.loanId, { resolution: value })}
                                      className="mt-1"
                                    />
                                    <div>
                                      <div className="font-semibold text-white text-sm">{label}</div>
                                      <div className="text-xs text-gray-400">{help}</div>
                                    </div>
                                  </label>
                                ))}
                              </div>

                              {d.resolution === 'discharged' && (
                                <div className="mt-4 pt-4 border-t border-gray-700 space-y-3">
                                  <div>
                                    <label className="block text-[0.62rem] uppercase tracking-wide text-gray-500 font-bold mb-1.5">
                                      Loss reason (required)
                                    </label>
                                    <select
                                      value={d.reason}
                                      onChange={(e) => setDraft(loan.loanId, { reason: e.target.value })}
                                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value="">Select reason…</option>
                                      {LOSS_REASONS.map((r) => (
                                        <option key={r} value={r}>{LOSS_REASON_LABELS[r]}</option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              )}

                              <div className="mt-3">
                                <label className="block text-[0.62rem] uppercase tracking-wide text-gray-500 font-bold mb-1.5">
                                  Notes (optional)
                                </label>
                                <textarea
                                  value={d.notes}
                                  onChange={(e) => setDraft(loan.loanId, { notes: e.target.value })}
                                  rows={2}
                                  placeholder="Context for this decision…"
                                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                                />
                              </div>

                              <button
                                onClick={() => handleResolve(loan)}
                                disabled={
                                  !d.resolution ||
                                  (d.resolution === 'discharged' && !d.reason) ||
                                  savingId === loan.loanId
                                }
                                className="mt-4 inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-500 text-white rounded-lg px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Check className="w-3.5 h-3.5" />
                                {savingId === loan.loanId ? 'Resolving…' : 'Resolve loan'}
                              </button>
                            </div>

                            {/* RIGHT: contact timeline (shared canonical store) */}
                            <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                              <ContactTimeline
                                companyId={companyId}
                                loanId={loan.loanId}
                                customerName={loan.customerName}
                                loanBroker={loan.broker || undefined}
                                sourcePage="Discharges"
                                entries={contactsByLoan[loan.loanId] || []}
                                availableBrokers={contactBrokerList}
                                currentUser={currentUser}
                                onMutate={mutateContact}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DormantLoansSection;
